import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { inputStyle } from '@/components/ui';
import { Icon, I } from '@/components/icons';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * 全站統一的下拉選單：觸發鈕沿用 inputStyle，展開清單與顧客搜尋器同款
 * （白底、圓角、陰影、選中高亮）。取代無法被 CSS 樣式化的原生 <select>。
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = '請選擇',
  disabled,
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...inputStyle,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          background: 'var(--surface)',
        }}
      >
        <span
          style={{
            color: selected ? 'var(--ink)' : 'var(--ink-4)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selected?.label ?? placeholder}
        </span>
        <Icon d={I.arrowDown} size={12} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            // 至少與觸發鈕同寬,但可長到容納選項文字(避免長療程名被擠成兩行)
            minWidth: '100%',
            maxWidth: 360,
            width: 'max-content',
            maxHeight: 240,
            overflow: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 60,
          }}
        >
          {options.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>無選項</div>
          )}
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              disabled={o.disabled}
              onClick={() => {
                if (o.disabled) return;
                onChange(o.value);
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                border: 0,
                borderBottom: '1px solid var(--line-soft)',
                background: o.value === value ? 'var(--primary-tint)' : 'transparent',
                color: o.disabled ? 'var(--ink-4)' : 'var(--ink)',
                fontWeight: o.value === value ? 600 : 400,
                fontSize: 12.5,
                cursor: o.disabled ? 'not-allowed' : 'pointer',
                // 不換行:長選項(療程名 / 員工帳號)維持單行,過長才以 … 截斷
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
